import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import {
  toApiErrorPayload,
  type ApiErrorPayload,
} from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

// ── 科目マスタ型 ──

export type BudgetCategoryType =
  | "income"
  | "expense"
  | "expense_opex"
  | "expense_capex"
  | "loan";

export type BudgetRevenueGroup = "stock_rent" | "stock_other" | "other";

export interface BudgetCategoryRecord {
  client_id: string;
  category_id: string;
  name: string;
  category_type: BudgetCategoryType;
  /**
   * 親子モデル (2026-07)。
   * "category"=カテゴリ(親, 予実の見出し。子を持てる/空も可)、
   * "item"=明細科目(子)。未設定は "item" とみなす。
   */
  level?: "category" | "item";
  /** 明細科目(子)の親カテゴリ category_id。level="item"(支出)のみ設定。 */
  parent_category_id?: string;
  /** 収入科目の上位カテゴリ (stock_rent=貸室 / stock_other=付帯 / other=その他)。支出系は無し。 */
  revenue_group?: BudgetRevenueGroup;
  /** 支出科目の上位カテゴリ (建物管理費/公租公課/工事費/PM報酬等/水道光熱費/保険料/その他 + 任意)。収入系は無し。 */
  expense_group?: string;
  display_order: number;
  is_system: boolean;
  created_at: string;
  updated_at?: string;
}

// ── 予実データ型 ──

export interface BudgetMonthData {
  /** null = 未入力。0 = 明示ゼロ。両者を区別する (§7-I)。 */
  budget: number | null;
  actual: number | null;
  actual_source?: "manual" | "auto" | "adjusted";
  adjustment?: number;
  memo?: string;
  /**
   * 月×フィールド単位の手動上書きフラグ。
   * - true のセルは内訳の自動再集計で値を上書きしない (手動値を保持)。
   * - 内訳合計が手動値を超える場合は保存時に BE で 400 が返る。
   */
  manual_override?: {
    budget?: boolean;
    actual?: boolean;
  };
}

export interface BudgetDetailRow {
  label: string;
  section_id?: string;
  /**
   * section_category code — リンク遷移先判定用。
   * "private_area" / "common_area" / "land" / "facility" / "other" or uuid。
   */
  section_category?: string;
  /** 収益種別 — "rent" | "ancillary_income" | "other_income" */
  revenue_type?: string;
  months: Record<string, BudgetMonthData>;
}

export interface BudgetRecord {
  client_id: string;
  sort_key: string;
  property_id: string;
  fiscal_year: string;
  category_id: string;
  category: string;
  category_type: BudgetCategoryType;
  months: Record<string, BudgetMonthData>;
  source?: "rent_roll" | "manual" | "repair";
  synced_at?: string;
  manual_override?: boolean;
  details?: BudgetDetailRow[];
  created_at: string;
  updated_at?: string;
}

// ── 科目マスタ Thunks ──

export const fetchBudgetCategories = createAsyncThunk<
  BudgetCategoryRecord[] | { items: BudgetCategoryRecord[] },
  void,
  { rejectValue: ApiErrorPayload }
>("budgets/fetchCategories", async (_, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/budget-categories`);
    return res.data.data;
  } catch (error: unknown) {
    return rejectWithValue(
      toApiErrorPayload(error, "Failed to fetch categories"),
    );
  }
});

// 予実科目はテーブル化され、ユーザー追加が可能 (段階2)。
// 収入科目は revenue_group (stock_rent / stock_other / other) が必須。
export const createBudgetCategory = createAsyncThunk<
  BudgetCategoryRecord,
  {
    name: string;
    category_type: BudgetCategoryType;
    /** "category"=カテゴリ(親) / "item"=明細科目(子)。省略時は parent_category_id 有無で判定。 */
    level?: "category" | "item";
    /** 明細科目(子)を作る場合の親カテゴリ category_id (支出)。 */
    parent_category_id?: string;
    revenue_group?: BudgetRevenueGroup;
    expense_group?: string;
    display_order?: number;
  },
  { rejectValue: ApiErrorPayload }
>("budgets/createCategory", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/budget-categories`,
      body,
    );
    return res.data.data;
  } catch (error: unknown) {
    return rejectWithValue(
      toApiErrorPayload(error, "Failed to create category"),
    );
  }
});

// 科目のリネーム / 表示順・収益カテゴリ変更 (システム標準科目は BE で 400)。
export const updateBudgetCategory = createAsyncThunk<
  { category_id: string } & Partial<BudgetCategoryRecord>,
  {
    category_id: string;
    name?: string;
    display_order?: number;
    revenue_group?: BudgetRevenueGroup;
    expense_group?: string;
    parent_category_id?: string;
  },
  { rejectValue: ApiErrorPayload }
>(
  "budgets/updateCategory",
  async ({ category_id, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/budget-categories/${category_id}`,
        body,
      );
      return res.data.data;
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(error, "Failed to update category"),
      );
    }
  },
);

// 科目の削除 (論理削除)。システム標準科目・予算レコードで使用中の科目は BE で拒否される。
export const deleteBudgetCategory = createAsyncThunk<
  { category_id: string; deleted: boolean },
  string,
  { rejectValue: ApiErrorPayload }
>("budgets/deleteCategory", async (category_id, { rejectWithValue }) => {
  try {
    const res = await ipponApi.delete(
      `${BaseURL}/api/v1/budget-categories/${category_id}`,
    );
    return res.data.data;
  } catch (error: unknown) {
    return rejectWithValue(
      toApiErrorPayload(error, "Failed to delete category"),
    );
  }
});

export const checkDeleteBudgetCategory = createAsyncThunk<
  {
    category_id: string;
    can_delete: boolean;
    reason?: string;
    usage_count?: number;
  },
  string,
  { rejectValue: ApiErrorPayload }
>("budgets/checkDeleteCategory", async (category_id, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(
      `${BaseURL}/api/v1/budget-categories/${category_id}/delete-check`,
    );
    return res.data.data;
  } catch (error: unknown) {
    return rejectWithValue(
      toApiErrorPayload(error, "Failed to check category deletion"),
    );
  }
});

// ── 予実データ Thunks ──

export const fetchBudgets = createAsyncThunk<
  BudgetRecord[] | { items: BudgetRecord[] },
  { property_id: string; fiscal_year: string },
  { rejectValue: ApiErrorPayload }
>(
  "budgets/fetch",
  async ({ property_id, fiscal_year }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.get(`${BaseURL}/api/v1/budget`, {
        params: { property_id, fiscal_year },
      });
      return res.data.data;
    } catch (error: unknown) {
      return rejectWithValue(
        toApiErrorPayload(error, "Failed to fetch budgets"),
      );
    }
  },
);

export const createBudget = createAsyncThunk<
  any,
  {
    property_id: string;
    fiscal_year: string;
    category_id: string;
    months?: Record<string, BudgetMonthData>;
    source?: string;
    details?: BudgetDetailRow[];
  }
>("budgets/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/budget`, body);
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to create budget");
  }
});

export const updateBudget = createAsyncThunk<
  any,
  {
    sort_key: string;
    months?: Record<string, BudgetMonthData>;
    details?: BudgetDetailRow[];
    source?: string;
    manual_override?: boolean;
  }
>("budgets/update", async ({ sort_key, ...body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/budget/${encodeURIComponent(sort_key)}`,
      body,
    );
    return { ...res.data.data, sort_key };
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to update budget");
  }
});

export const updateBudgetCategoryCell = createAsyncThunk<
  any,
  {
    property_id: string;
    fiscal_year: string;
    category_id: string;
    category_type?: BudgetCategoryType;
    month: string;
    field: "budget" | "actual";
    /** null = セルをクリア (未入力に戻す)。§7-I */
    amount: number | null;
    /** モーダルで手動採用して積み上げとの競合を解決した場合 true (以後 ⚠️ を出さない)。 */
    resolved?: boolean;
  }
>("budgets/updateCategoryCell", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/budget/category-cell`,
      body,
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to update budget cell",
    );
  }
});

// 内訳 (項目) の手動実績 (manual_actual) を set/clear する。
// value=number → 手動実績設定 / null → 入金管理に戻す (モーダル「入金を採用」)。
export const updateBudgetDetailManualActual = createAsyncThunk<
  any,
  {
    property_id: string;
    fiscal_year: string;
    category_id: string;
    category_type?: BudgetCategoryType;
    detail_label: string;
    month: string;
    value: number | null;
    // "manual" = モーダルで手動を採用 (解決済み → 警告を出さない)。
    // "auto"   = インライン入力など未解決 (入金と食い違えば警告)。
    actual_source?: "manual" | "auto";
  }
>("budgets/updateDetailManualActual", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/budget/detail-manual-actual`,
      body,
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to update detail manual actual",
    );
  }
});

export const deleteBudget = createAsyncThunk<any, { sort_key: string }>(
  "budgets/delete",
  async ({ sort_key }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(
        `${BaseURL}/api/v1/budget/${encodeURIComponent(sort_key)}`,
      );
      return sort_key;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to delete budget");
    }
  },
);

export const syncRentRoll = createAsyncThunk<
  any,
  { property_id: string; fiscal_year: string }
>("budgets/syncRentRoll", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/budget/sync-rent-roll`,
      body,
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to sync rent roll");
  }
});

// ── CSV 一括アップロード ──

export interface BulkBudgetRow {
  fiscal_year: string;
  group: "収入" | "支出";
  /** カテゴリ列 (収入=貸室/付帯/その他, 支出=建物管理費/公租公課/…)。CSV v3.2。 */
  category_group?: string;
  category: string;
  row_kind: "予算" | "実績";
  months: Record<string, number | null>;
}

export interface BulkUploadBudgetsBody {
  property_id: string;
  fiscal_year: string;
  mode?: "merge" | "replace";
  rows: BulkBudgetRow[];
}

export const bulkUploadBudgets = createAsyncThunk<
  {
    created_categories: BudgetCategoryRecord[];
    upserted_budgets: number;
    expenses_created: number;
    expenses_updated: number;
    warnings: { line?: number; message: string }[];
  },
  BulkUploadBudgetsBody
>("budgets/bulkUpload", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/budget/bulk`, body);
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to bulk upload");
  }
});

// ── State ──

interface BudgetState {
  categories: {
    data: BudgetCategoryRecord[];
    loading: boolean;
    error: ApiErrorPayload | null;
  };
  list: {
    data: BudgetRecord[];
    loading: boolean;
    error: ApiErrorPayload | null;
    loaded: boolean;
  };
  syncing: boolean;
}

const initialState: BudgetState = {
  categories: { data: [], loading: false, error: null },
  list: { data: [], loading: false, error: null, loaded: false },
  syncing: false,
};

const budgetSlice = createSlice({
  name: "budgets",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // 科目マスタ
      .addCase(fetchBudgetCategories.pending, (state) => {
        state.categories.loading = true;
        state.categories.error = null;
      })
      .addCase(fetchBudgetCategories.fulfilled, (state, action) => {
        state.categories.loading = false;
        state.categories.data = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
      })
      .addCase(fetchBudgetCategories.rejected, (state, action) => {
        state.categories.loading = false;
        state.categories.error = action.payload ?? null;
      })
      .addCase(createBudgetCategory.fulfilled, (state, action) => {
        // 作成した科目を即座に一覧へ反映 (再取得を待たずプルダウン/内訳に出せる)
        if (action.payload && action.payload.category_id) {
          const exists = state.categories.data.some(
            (c) => c.category_id === action.payload.category_id,
          );
          if (!exists) state.categories.data.push(action.payload);
        }
      })
      .addCase(updateBudgetCategory.fulfilled, (state, action) => {
        const updated = action.payload;
        if (updated?.category_id) {
          state.categories.data = state.categories.data.map((c) =>
            c.category_id === updated.category_id ? { ...c, ...updated } : c,
          );
        }
      })
      .addCase(deleteBudgetCategory.fulfilled, (state, action) => {
        const id = action.payload?.category_id;
        if (id) {
          state.categories.data = state.categories.data.filter(
            (c) => c.category_id !== id,
          );
        }
      })
      // 予実データ
      .addCase(fetchBudgets.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || [];
        state.list.loaded = true;
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload ?? null;
      })
      .addCase(createBudget.fulfilled, (state, action) => {
        state.list.data = [action.payload, ...state.list.data];
      })
      .addCase(updateBudget.fulfilled, (state, action) => {
        const updated = action.payload;
        if (updated?.sort_key) {
          state.list.data = state.list.data.map((b) =>
            b.sort_key === updated.sort_key ? { ...b, ...updated } : b,
          );
        }
      })
      .addCase(updateBudgetCategoryCell.fulfilled, (state, action) => {
        const updated = action.payload;
        if (updated?.sort_key) {
          state.list.data = state.list.data.map((b) =>
            b.sort_key === updated.sort_key ? { ...b, ...updated } : b,
          );
          if (!state.list.data.some((b) => b.sort_key === updated.sort_key)) {
            state.list.data.push(updated);
          }
        }
      })
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (b) => b.sort_key !== action.payload,
        );
      })
      .addCase(syncRentRoll.pending, (state) => {
        state.syncing = true;
      })
      .addCase(syncRentRoll.fulfilled, (state, action) => {
        state.syncing = false;
        const synced = action.payload?.items || [];
        for (const item of synced) {
          const idx = state.list.data.findIndex(
            (b) => b.sort_key === item.sort_key,
          );
          if (idx >= 0) {
            state.list.data[idx] = item;
          } else {
            state.list.data.push(item);
          }
        }
      })
      .addCase(syncRentRoll.rejected, (state) => {
        state.syncing = false;
      });
  },
});

export default budgetSlice.reducer;
